import type { IconName } from "@/components/ui/icon";

/**
 * Shared source of truth for the Search/Insights tabs on a talent-search
 * session. Each tab is its own route — `/talent-search/{searchId}` (search)
 * and `/talent-search/{searchId}/insights` — consumed by both the layout's
 * `SearchTabsNav` `TabsList` and the mobile drawer's `SearchTabsPanel`
 * drill-down, so the two surfaces never drift. The list ⇄ table toggle for
 * results lives inside the Search tab (`SearchViewSwitcher`), not as a route.
 */
export type SearchTab = "search" | "insights";

export const TALENT_SEARCH_ROOT = "/talent-search";

export const TABS: {
  id: SearchTab;
  label: string;
  icon: IconName;
}[] = [
  { id: "search", label: "Search", icon: "IconSearchIntelligence" },
  { id: "insights", label: "Insights", icon: "IconInsights" },
];

/** Distinct from `resolveActiveTab` in the jobs `navigation-tabs.tsx`, which
 *  maps `/jobs/{id}` routes — same name, different route domain and signature
 *  by design (ARBI-851). */
export function resolveActiveTab(pathname: string): SearchTab {
  if (pathname.endsWith("/insights")) return "insights";
  return "search";
}

export function tabHref(searchId: string, tab: SearchTab): string {
  const base = `${TALENT_SEARCH_ROOT}/${searchId}`;
  if (tab === "search") return base;
  return `${base}/${tab}`;
}

export interface SearchRouteMatch {
  searchId: string | null;
  activeTab: SearchTab;
}

const SEARCH_ROUTE_RE =
  /^\/talent-search(?:\/([^/]+)(?:\/(insights))?)?(?:\/|$)/;

/**
 * Matches a pathname against the talent-search routes — the hero
 * (`/talent-search`) and a saved search's tabs
 * (`/talent-search/{searchId}[/insights]`) — and extracts the search id
 * (`null` on the hero) and active tab. Returns `null` for everything else,
 * mirroring `matchSequenceRoute`'s shape so `main-content.tsx` can gate the
 * mobile "Search" drill row the same way it gates "Sequence".
 */
export function matchSearchRoute(
  pathname: string | null | undefined,
): SearchRouteMatch | null {
  if (!pathname) return null;
  const match = SEARCH_ROUTE_RE.exec(pathname);
  if (!match) return null;
  const [, searchId, tab] = match;
  return {
    searchId: searchId ?? null,
    activeTab: (tab as SearchTab | undefined) ?? "search",
  };
}
